/* eslint-disable react/forbid-prop-types */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { bool, params, shape, string, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useImpression } from '@thd-olt-component-react/impression';
import classNames from 'classnames/bind';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { formatTextWithZeroWidthSpace, getCustomUrlWithAnalytics } from '../../productDetailsUtils';

const CONFIGURABLE_BLINDS = 'CONFIGURABLE_BLINDS';
const BUILD_AND_BUY = 'Build and Buy';
export const ProductHeader = ({
  itemId,
  brand = 'below',
  collectionBlock = true,
  disableShopThisCollection = false,
  large = false,
  target = null,
  brandMaxLine = null,
  brandTitleMaxLine = null,
  isIframe = false,
  iframeAnalyticsData = null,
  titleMaxLine = null,
  fullWidth = false,
  xSmall = false,
  onClick = () => {},
  merchQueryParam = null,
  brandFontSize = null,
  brandLineHeight = null,
  productNameFontSize = null,
  productNameLineHeight = null
}) => {

  const { channel, hosts } = useContext(ExperienceContext);

  const CustomHeaderTag = useConfigService('fs:specialBuyHeaderTag') ? 'div' : 'h3';

  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'ProductDetails',
      name: 'ProductDetails',
      position: 0,
    },
  });

  const { parent } = additionalData || {};

  const [sponsoredValues, setSponsoredValues] = useState({});
  // part of tnt code see note below
  const [newTitleName, setNewTitleName] = useState({});

  const [productState, setProductState] = useState({
    canonicalUrl: '',
    productType: '',
    productLabel: '',
    brandName: '',
  });

  const { data } = useDataModel('product', {
    variables: {
      itemId,
    },
  });

  const { product } = data || {};

  const { details, identifiers, info } = product || {};

  const { collection: collectionObj } = details || {};

  useEffect(() => {
    if (identifiers?.productLabel) {
      setProductState({
        canonicalUrl: identifiers?.canonicalUrl,
        productType: identifiers?.productType,
        productLabel: identifiers?.productLabel,
        brandName: identifiers?.brandName,
      });
    }
  }, [identifiers]);

  useEffect(() => {
    if (info) {
      setSponsoredValues({
        isSponsored: sponsoredValues?.isSponsored || info?.isSponsored,
        sponsoredMetadata: sponsoredValues?.sponsoredMetadata || info?.sponsoredMetadata,
      });
    }
  }, [info?.isSponsored, info?.sponsoredMetadata]);

  // this is start of temporary code for tnt take out when tnt is done
  // tnt est start 4/24/23
  const output = useLifeCycleEventBus('product-name_PN120-testing', true);

  useEffect(() => {
    const incomingName = output[itemId];
    if (incomingName) {
      const sampleObj = {};
      sampleObj[itemId] = incomingName;
      setNewTitleName({ ...newTitleName, ...sampleObj });
      setProductState({
        ...productState,
        productLabel: incomingName,
      });
    }
  }, [output, itemId]);
  // end of temp tnt code
  const { name: collectionName, url: collectionUrl } = collectionObj || {};

  const canonicalUrl = identifiers?.canonicalUrl || productState.canonicalUrl;
  const productType = identifiers?.productType || productState.productType;
  const productLabel = newTitleName[itemId] || identifiers?.productLabel || productState.productLabel;
  const brandName = identifiers?.brandName || productState.brandName;

  if (!productLabel) return null;

  const handleClick = (event, action = null) => {
    const customExperience = !!info?.globalCustomConfigurator?.customExperience;
    if (!customExperience && onClick && action !== BUILD_AND_BUY) {
      onClick(event, product, action, sponsoredValues);
    }
    const productHeaderEventData = {
      podAction: action || 'product details',
      podAnchorSku: itemId,
      target,
      parent,
    };

    // Guard against SSR for window global access
    if (typeof window !== 'undefined') {
      if (isIframe && window.top?.LIFE_CYCLE_EVENT_BUS) {
        window.top.LIFE_CYCLE_EVENT_BUS.trigger('cartItem.DD_EVENT', iframeAnalyticsData);
      } else if (window.LIFE_CYCLE_EVENT_BUS) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', productHeaderEventData);
      }
    }
  };

  const shouldDisplayBrand = (location) => {
    return brandName && !brandName.toLowerCase().includes('unbranded') && brand === location;
  };

  const isBuildAndBuyProduct = productType === CONFIGURABLE_BLINDS;

  const isMobile = channel === 'mobile';
  const showShopThisCollectionLink = !disableShopThisCollection && collectionName && collectionUrl;
  const onBuildAndBuyImageClick = (event) => handleClick(event, BUILD_AND_BUY);
  const checkOnClick = isBuildAndBuyProduct ? onBuildAndBuyImageClick : handleClick;

  const productURL = getCustomUrlWithAnalytics({
    canonicalUrl,
    isBuildAndBuyProduct,
    blindsHost: hosts?.customBlinds,
    sponsoredValues: sponsoredValues?.isSponsored
      ? sponsoredValues
      : {
        isSponsored: info?.isSponsored,
        sponsoredMetadata: info?.sponsoredMetadata,
      },
    merchQueryParam: merchQueryParam || null
  });

  const headerTarget = isMobile && !isIframe ? '' : target;

  const inlineClasses = `sui-line-clamp-${titleMaxLine} sui-mt-px sui-mb-px`;
  const defaultClasses = 'sui-line-clamp-5 sui-mt-1';
  const commonClasses = {
    'sui-text-primary sui-font-regular sui-text-ellipsis': brand === 'inline' || brand === 'above',
    'sui-text-lg sui-inline': large,
    'sui-w-full': fullWidth,
    'sui-block': brand !== 'inline',
    'sui-leading-tight': brand === 'inline',
    'sui-mb-1': brand === 'above'
  };

  const titleClasses = classNames('sui-flex-col', brand === 'inline' ? inlineClasses : defaultClasses, commonClasses);
  const labelClasses = classNames(
    'sui-text-primary sui-font-regular sui-text-ellipsis',
    `${productNameFontSize ? `sui-text-${productNameFontSize}` : 'sui-text-sm'}`,
    `${productNameLineHeight ? `sui-leading-${productNameLineHeight}` : 'sui-leading-normal'}`,
    `${titleMaxLine ? `sui-line-clamp-${titleMaxLine}` : 'sui-line-clamp-5'}`,
    {
      'sui-line-clamp-2 sui-text-xs sui-m-0': xSmall && isMobile,
      'sui-inline': brand === 'inline'
    }
  );
  const brandClasses = classNames('sui-text-primary sui-font-w-bold', {
    'sui-whitespace-nowrap sui-overflow-hidden sui-text-ellipsis sui-h-full': brandMaxLine === 1,
    'sui-m-0': xSmall && isMobile,
  });
  const shopThisCollectionClasses = classNames('sui-text-primary sui-mt-1', {
    'sui-block': brand === 'inline' && collectionBlock,
  });
  const brandTitleClasses = classNames({
    'sui-inline': !brandTitleMaxLine,
    'sui-overflow-hidden sui-text-ellipsis': !!brandTitleMaxLine,
    'sui-line-clamp-2': brandTitleMaxLine === 2,
    'sui-line-clamp-3': brandTitleMaxLine === 3,
    'sui-line-clamp-4': brandTitleMaxLine === 4,
  });

  const brandNameClasses = classNames(
    'sui-text-primary sui-font-w-bold',
    `${brandFontSize ? `sui-text-${brandFontSize}` : 'sui-text-xs'}`,
    `${brandLineHeight ? `sui-leading-${brandLineHeight}` : 'sui-leading-tight'}`,
    {
      'sui-inline sui-mr-1': brand === 'inline',
      'sui-mb-1': brand === 'above',
    }
  );

  return (
    <>
      <div data-testid="product-header" data-component="ProductHeader">
        <Link href={productURL} onClick={checkOnClick} target={headerTarget} underline="hover">
          <div className={brandTitleClasses}>
            <CustomHeaderTag className={titleClasses}>
              {shouldDisplayBrand('above') && (
                <Typography
                  translate="no"
                  data-testid="attribute-brandname-above"
                  variant={brandFontSize ? 'body-' + brandFontSize : 'body-sm'}
                  weight="bold"
                >
                  {` ${brandName} `}
                </Typography>
              )}
              {shouldDisplayBrand('inline') && (
                <span
                  translate="no"
                  data-testid="attribute-brandname-inline"
                  className={brandNameClasses}
                >
                  {brandName}
                </span>
              )}
              <span data-testid="attribute-product-label" className={labelClasses}>
                {formatTextWithZeroWidthSpace(productLabel)}
              </span>
            </CustomHeaderTag>
          </div>
        </Link>
        {shouldDisplayBrand('below') && (
          <p className={brandClasses}>
            by
            <span
              translate="no"
              data-testid="attribute-brandname-below"
              className={brandNameClasses}
            >
              {' '}
              {brandName}
            </span>
          </p>
        )}
      </div>
      {showShopThisCollectionLink && (
        <Link
          className={shopThisCollectionClasses}
          href={collectionUrl}
          target={target}
          underline="hover"
        >
          <p data-testid="attribute-shop-this-collection" className="sui-text-xs sui-mt-1">
            Shop this Collection
          </p>
        </Link>
      )}
    </>
  );
};

ProductHeader.propTypes = {
  itemId: PropTypes.string.isRequired,
  /** Position the brand name */
  brand: PropTypes.oneOf(['above', 'below', 'hidden', 'inline']),
  collectionBlock: PropTypes.bool,
  disableShopThisCollection: PropTypes.bool,
  /** Larger text for title */
  large: PropTypes.bool,
  target: PropTypes.string,
  fullWidth: PropTypes.bool,
  titleMaxLine: PropTypes.number,
  brandMaxLine: PropTypes.number,
  brandTitleMaxLine: PropTypes.number,
  iframeAnalyticsData: PropTypes.object,
  isIframe: PropTypes.bool,
  xSmall: PropTypes.bool,
  onClick: PropTypes.func,
  merchQueryParam: PropTypes.string,
  /** Please check Stencil website to view the available values for the following props:
   * brandFontSize | productNameFontSize:
   * https://brand.homedepot.com/site/knapsack-nucleus/latest/pattern/typography-style?subPageId=Utility-Classes
   * scroll down to #Font Size
   * brandLineHeight | productNameLineHeight:
   * https://brand.homedepot.com/site/knapsack-nucleus/latest/pattern/typography-style?subPageId=Utility-Classes
   * scroll down to #Line Height
  */
  brandFontSize: PropTypes.string,
  brandLineHeight: PropTypes.string,
  productNameFontSize: PropTypes.string,
  productNameLineHeight: PropTypes.string
};

ProductHeader.displayName = 'ProductHeader';

ProductHeader.dataModel = {
  product: params({
    itemId: string().isRequired(),
  }).shape({
    itemId: string(),
    dataSource: string(),
    details: shape({
      collection: shape({
        name: string(),
        url: string(),
      }),
    }),
    identifiers: shape({
      canonicalUrl: string(),
      brandName: string(),
      productType: string(),
      productLabel: string(),
    }),
    info: shape({
      isSponsored: bool(),
      sponsoredMetadata: shape({
        campaignId: string(),
        placementId: string(),
        slotId: string(),
      }),
      globalCustomConfigurator: shape({
        customExperience: string(),
      }),
    }),
  }),
};
